import type { TravelAllowanceOptions } from '../../types/tax';

// 2024/2025 Travel allowance and fringe benefit rates
export const TRAVEL_ALLOWANCE_RATES = {
  // Deemed benefit rates for private use of company vehicles
  deemedBenefitRate: 0.035, // 3.5% per month of determined value
  // Travel allowance reasonable rates (per km) - these are guidelines
  reasonableRates: {
    petrol: 4.24, // R4.24 per km for vehicles using petrol
    diesel: 3.82, // R3.82 per km for vehicles using diesel
  },
} as const;

export interface TravelAllowanceCalculationResult {
  travelAllowanceAmount: number;
  deemedBenefit: number;
  taxableAmount: number;
  monthlyDeemedBenefit: number;
  privateUsePercentage: number;
}

/**
 * Calculate travel allowance and fringe benefit for company vehicles
 * Based on SARS 2024/2025 regulations
 * 
 * @param options Travel allowance options
 * @returns Travel allowance calculation result
 */
export function calculateTravelAllowance(
  options: TravelAllowanceOptions
): TravelAllowanceCalculationResult {
  if (!options.hasTravelAllowance) {
    return {
      travelAllowanceAmount: 0,
      deemedBenefit: 0,
      taxableAmount: 0,
      monthlyDeemedBenefit: 0,
      privateUsePercentage: 0,
    };
  }

  const { vehicleValue, privateUsePercentage, travelAllowanceAmount } = options;

  // Calculate deemed benefit for private use of company vehicle
  // Deemed benefit = 3.5% per month of determined value × private use percentage
  const monthlyDeemedBenefit = (vehicleValue * TRAVEL_ALLOWANCE_RATES.deemedBenefitRate) / 12;
  const adjustedMonthlyDeemedBenefit = monthlyDeemedBenefit * (privateUsePercentage / 100);
  const deemedBenefit = adjustedMonthlyDeemedBenefit * 12;

  // Travel allowance is generally fully taxable unless properly substantiated
  // For simplicity, we're treating it as fully taxable
  const taxableAmount = travelAllowanceAmount + deemedBenefit;

  return {
    travelAllowanceAmount,
    deemedBenefit,
    taxableAmount,
    monthlyDeemedBenefit: adjustedMonthlyDeemedBenefit,
    privateUsePercentage,
  };
}

/**
 * Calculate the additional taxable income from travel allowance and fringe benefits
 * This amount gets added to gross income for tax calculation purposes
 * 
 * @param options Travel allowance options
 * @returns Additional taxable income amount
 */
export function calculateTravelAllowanceTaxableIncome(
  options: TravelAllowanceOptions
): number {
  const result = calculateTravelAllowance(options);
  return result.taxableAmount;
}

/**
 * Validate travel allowance options
 * @param options Travel allowance options to validate
 * @returns Validation result
 */
export function validateTravelAllowanceOptions(
  options: TravelAllowanceOptions
): {
  isValid: boolean;
  message?: string;
} {
  if (!options.hasTravelAllowance) {
    return { isValid: true };
  }

  // Validate vehicle value
  if (options.vehicleValue < 0) {
    return {
      isValid: false,
      message: 'Vehicle value cannot be negative',
    };
  }

  if (options.vehicleValue > 5000000) {
    return {
      isValid: false,
      message: 'Vehicle value seems unusually high (above R5,000,000)',
    };
  }

  // Validate private use percentage
  if (options.privateUsePercentage < 0 || options.privateUsePercentage > 100) {
    return {
      isValid: false,
      message: 'Private use percentage must be between 0% and 100%',
    };
  }

  // Validate travel allowance amount
  if (options.travelAllowanceAmount < 0) {
    return {
      isValid: false,
      message: 'Travel allowance amount cannot be negative',
    };
  }

  if (options.travelAllowanceAmount > 500000) {
    return {
      isValid: false,
      message: 'Travel allowance amount seems unusually high (above R500,000 annually)',
    };
  }

  return { isValid: true };
}